import type { AnimationMeta, AnimationState, FrameEvent } from './schema';

/**
 * Pure, clock-driven animation state machine (spec.md §N.3). It owns no timers and
 * decodes no images — callers pass an absolute time in ms to {@link AnimationPlayer.update}
 * (in production the dungeon stage's requestAnimationFrame loop; in tests a fake clock).
 *
 * Frame model: a state advances one "frame step" every `1000/fps` ms. The displayed
 * sheet index is `frames[step % frames.length]` for looping states and
 * `frames[min(step, frames.length-1)]` for non-looping ones (which clamp/hold on the
 * last frame). Frame events fire **once per occurrence**: a monotonic step counter is
 * the occurrence id, so a looping event re-fires on each loop while a non-looping one
 * fires exactly once, even if a single `update` spans several frames.
 */

export type Facing = 'left' | 'right';

export interface AnimationPlayerOptions {
  /** Absolute ms the initial (default) state is anchored to. Defaults to 0. */
  now?: number;
  facing?: Facing;
}

export interface PlayOptions {
  /** Restart the state from frame 0 even if it is already the current state. */
  force?: boolean;
}

export class AnimationPlayer {
  private stateName: string;
  private stateStartedAtMs: number;
  /** Highest frame-step index whose events have already been emitted (-1 = none yet). */
  private lastEmittedStep = -1;
  /** Current frame-step at the most recent update (monotonic within a state). */
  private currentStep = 0;
  private facing: Facing;

  constructor(
    private readonly meta: AnimationMeta,
    options: AnimationPlayerOptions = {},
  ) {
    this.stateName = meta.defaultState;
    this.stateStartedAtMs = options.now ?? 0;
    this.facing = options.facing ?? 'right';
  }

  getStateName(): string {
    return this.stateName;
  }

  getFacing(): Facing {
    return this.facing;
  }

  setFacing(facing: Facing): void {
    this.facing = facing;
  }

  private state(): AnimationState {
    return this.meta.states[this.stateName]!;
  }

  /** True once a non-looping state has displayed all its frames for their full duration. */
  isFinished(): boolean {
    const state = this.state();
    if (state.loop) return false;
    return this.currentStep >= state.frames.length;
  }

  /** Whether the metadata flags this state to hold its final frame on completion. */
  holdsLastFrame(): boolean {
    return this.state().holdLastFrame === true;
  }

  /** Transition to a state, resetting its frame clock (unless it is already current and not forced). */
  play(stateName: string, nowMs: number, opts: PlayOptions = {}): void {
    if (!this.meta.states[stateName]) {
      throw new Error(`Unknown animation state "${stateName}"`);
    }
    if (stateName === this.stateName && !opts.force) return;
    this.stateName = stateName;
    this.stateStartedAtMs = nowMs;
    this.lastEmittedStep = -1;
    this.currentStep = 0;
  }

  /** The sheet frame index currently displayed. */
  getCurrentFrame(): number {
    const state = this.state();
    const index = state.loop
      ? this.currentStep % state.frames.length
      : Math.min(this.currentStep, state.frames.length - 1);
    return state.frames[index]!;
  }

  /**
   * Advance the playhead to absolute time `nowMs`. Returns the frame events that
   * newly fired during this step (possibly empty), each at most once per occurrence.
   */
  update(nowMs: number): FrameEvent[] {
    const state = this.state();
    const frameDurationMs = 1000 / state.fps;
    const elapsed = Math.max(0, nowMs - this.stateStartedAtMs);
    this.currentStep = Math.floor(elapsed / frameDurationMs);

    // Non-looping events never fire past the final frame's occurrence.
    const maxEmitStep = state.loop
      ? this.currentStep
      : Math.min(this.currentStep, state.frames.length - 1);

    const emitted: FrameEvent[] = [];
    if (state.events && state.events.length > 0) {
      for (let step = this.lastEmittedStep + 1; step <= maxEmitStep; step += 1) {
        const sheetFrame = state.frames[step % state.frames.length]!;
        for (const ev of state.events) {
          if (ev.frame === sheetFrame) emitted.push(ev);
        }
      }
    }
    if (maxEmitStep > this.lastEmittedStep) this.lastEmittedStep = maxEmitStep;
    return emitted;
  }
}
